import { supabase } from '../supabase/client.js';
import { listPlaybackProgress, mergePlaybackProgress, viewerProgressScope } from './playbackService.js';

const COMPLETE_RATIO = 0.95;
const COMPLETE_REMAINING_SECONDS = 20;

function requireClient() {
  if (!supabase) throw new Error('WatchVIM cloud progress is not configured.');
  return supabase;
}

function toMillis(value) {
  const millis = Date.parse(value || '');
  return Number.isFinite(millis) ? millis : 0;
}

function normalizeRow(row) {
  const seconds = Number(row?.position_seconds) || 0;
  const duration = Number(row?.duration_seconds) || 0;
  if (!row?.content_key || seconds < 1 || row?.completed === true) return null;
  return {
    mediaKey: String(row.content_key),
    seconds,
    duration,
    updatedAt: toMillis(row.updated_at),
  };
}

export async function loadCloudProgress(user) {
  if (!user?.id) return [];
  const client = requireClient();
  const { data, error } = await client
    .from('watchvim_playback_progress')
    .select('content_key,position_seconds,duration_seconds,completed,updated_at')
    .eq('user_id', user.id)
    .eq('completed', false)
    .order('updated_at', { ascending: false })
    .limit(250);
  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(normalizeRow).filter(Boolean);
}

export async function saveCloudProgress(user, media, seconds, duration = 0) {
  if (!user?.id || !media?.mediaKey || media?.kind === 'trailer') return false;
  const current = Math.max(0, Number(seconds) || 0);
  const total = Math.max(0, Number(duration) || 0);
  if (current < 1) return true;

  if (total > 0) {
    const ratio = current / total;
    const remaining = total - current;
    if (ratio >= COMPLETE_RATIO || remaining <= COMPLETE_REMAINING_SECONDS) {
      return clearCloudProgress(user, media.mediaKey);
    }
  }

  const client = requireClient();
  const row = {
    user_id: user.id,
    content_key: media.mediaKey,
    title_id: media.titleId || media.parentTitleId || null,
    parent_title_id: media.parentTitleId || null,
    kind: media.kind || 'content',
    position_seconds: Math.floor(current),
    duration_seconds: total > 0 ? Math.floor(total) : 0,
    completed: false,
    updated_at: new Date().toISOString(),
  };
  const { error } = await client
    .from('watchvim_playback_progress')
    .upsert(row, { onConflict: 'user_id,content_key' });
  if (error) throw error;
  return true;
}

export async function clearCloudProgress(user, mediaKey) {
  if (!user?.id || !mediaKey) return false;
  const client = requireClient();
  const { error } = await client
    .from('watchvim_playback_progress')
    .delete()
    .eq('user_id', user.id)
    .eq('content_key', mediaKey);
  if (error) throw error;
  return true;
}

export async function synchronizeViewerProgress(user) {
  if (!user?.id) return { cloud: [], pushed: 0 };
  const scope = viewerProgressScope(user);
  const cloud = await loadCloudProgress(user);
  const localBefore = listPlaybackProgress(scope);
  mergePlaybackProgress(scope, cloud);

  const cloudMap = new Map(cloud.map((record) => [record.mediaKey, record]));
  let pushed = 0;
  for (const local of localBefore) {
    const remote = cloudMap.get(local.mediaKey);
    if (remote && remote.updatedAt >= local.updatedAt) continue;
    const media = {
      mediaKey: local.mediaKey,
      kind: local.mediaKey.startsWith('episode:') ? 'episode' : 'content',
      titleId: local.mediaKey.startsWith('title:') ? local.mediaKey.split(':')[1] : null,
      parentTitleId: local.mediaKey.startsWith('episode:') ? local.mediaKey.split(':')[1] : null,
    };
    await saveCloudProgress(user, media, local.seconds, local.duration);
    pushed += 1;
  }
  return { cloud, pushed };
}
